import { atom } from "jotai";
import {
  isAgentsEnabledAtom,
  isBlankCanvasAtom,
  isDraftModeIncludingTemplatesAtom,
  formsTerminologyAtom,
} from "@zapier/canvas-state";
import { Message } from "@ai-sdk/ui-utils";
import { aiPromptAtom } from "@zapier/streaming";

// Generate dynamic initial messages based on URL prompt parameter
export const initialMessagesAtom = atom((get) => {
  const prompt = get(aiPromptAtom);
  const isDraftModeIncludingTemplates = get(isDraftModeIncludingTemplatesAtom);
  const isBlankCanvas = get(isBlankCanvasAtom);
  const isAgentsEnabled = get(isAgentsEnabledAtom);

  const interfacesTerms = get(formsTerminologyAtom);

  const BASE_ASSET_TYPES = `Zap workflows, Tables, ${interfacesTerms.plural} and Chatbots`;

  const ASSET_TYPES = isAgentsEnabled
    ? `Agents, ${BASE_ASSET_TYPES}`
    : BASE_ASSET_TYPES;

  // If there's a prompt from the URL, create a user message with that prompt
  if (prompt && typeof prompt === "string" && prompt.trim()) {
    const decodedPrompt = decodeURIComponent(prompt.trim());
    return [
      {
        role: "assistant",
        content: decodedPrompt, // Assistant will invoke the tool next
        id: "assistant-tool-call",
        createdAt: new Date(),
        parts: [
          { type: "text", text: decodedPrompt },
          {
            type: "tool-invocation",
            toolInvocation: {
              state: "call",
              toolName: "create_system",
              toolCallId: "create_system",
              args: {
                prompt,
              },
            },
          },
        ],
      } as Message,
    ];
  }

  // Show simple welcome for blank/new canvas (persistent buttons will show suggestions)
  if (isBlankCanvas) {
    return [
      {
        id: "canvas-welcome",
        role: "assistant" as const,
        content: `I'm here to help you build and edit Zapier workflows on your canvas. I can help you create systems using ${ASSET_TYPES}. I can also edit existing components and work with Zap workflows.`,
      },
    ];
  }

  // Show contextual welcome for canvas with content but not in draft mode
  if (!isDraftModeIncludingTemplates) {
    return [
      {
        id: "canvas-welcome-build-mode",
        role: "assistant" as const,
        content: `I'm here to help you build and edit Zapier workflows on your canvas. I can help you create systems using ${ASSET_TYPES}. I can also edit existing components and work with Zap workflows.\n\nHere are some quick actions to get started:`,
      },
    ];
  }

  // Default welcome message when no prompt is provided
  return [
    {
      id: "canvas-welcome",
      role: "assistant" as const,
      content: `I'm here to help you build and edit Zapier workflows on your canvas. I can help you create systems using ${ASSET_TYPES}. I can also edit existing components and work with Zap workflows.

What would you like to do?`,
    },
  ];
});
initialMessagesAtom.debugLabel = "initialMessagesAtom";
